import React, { createContext, useContext, useMemo, useState } from 'react';
import { Cross1Icon } from '@radix-ui/react-icons';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '../../components/Common/UI/Drawer';
import { Button } from '../../components/Common/UI/Button';
import { ScrollArea } from '../../components/Common/UI/ScrollArea';
import { Input } from '../../components/Common/UI/Input';
import Icons from '../../components/Common/Icons/Icons';
import { useJobDetailsProvider } from './jobDetails';
import Separator from '../../components/Common/UI/Separator';
import { JobSubTableAccount } from '../components/Pages/JobStatus/Details/TabsContent/subTableConfig';

/** Search helper functions: */
export type SearchKeyType = 'accountNumber' | 'accountName' | 'accountType' | 'IMB' | 'address';
export enum SearchKeyEnum {
  accountNumber = 'Account Number',
  accountName = 'Name',
  accountType = 'Account Type',
  IMB = 'IMB',
  address = 'Full Address',
}

export function generateSearchResults(
  searchQuery: string,
  data: JobSubTableAccount[],
): Record<SearchKeyType, JobSubTableAccount[]> {
  const searchResults: Record<SearchKeyType, JobSubTableAccount[]> = {
    accountNumber: [],
    accountName: [],
    accountType: [],
    IMB: [],
    address: [],
  };

  if (searchQuery.trim() !== '') {
    data.forEach(item => {
      Object.entries(item).forEach(([key, value]) => {
        if (
          (typeof value === 'string' && value.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (typeof value === 'number' && String(value).includes(searchQuery))
        ) {
          if (key in searchResults) {
            searchResults[key as SearchKeyType].push(item);
          }
        }
      });
    });
  }

  return searchResults;
}

/** Components: */
interface SearchAppliedProps {
  search: string | number;
  field: SearchKeyType | '';
}
interface GlobalSearchProviderProps {
  open: boolean;
  handleOpen: (open: boolean) => void;

  searchTemp: string;
  searchApplied: SearchAppliedProps;
  handleSearchTemp: (val: string) => void;
  handleSearchApplied: (valueObj: SearchAppliedProps) => void;

  content: React.ReactNode | null;
  handleContent: (content: React.ReactNode) => void;
}

const defaultValue: GlobalSearchProviderProps = {
  open: false,
  handleOpen: () => undefined,

  searchTemp: '',
  searchApplied: { search: '', field: '' },
  handleSearchTemp: () => undefined,
  handleSearchApplied: () => undefined,

  content: null,
  handleContent: () => undefined,
};

export const GlobalSearchContext = createContext<GlobalSearchProviderProps>(defaultValue);

function GlobalSearchProvider({ children }: React.PropsWithChildren) {
  const [open, setOpen] = useState<boolean>(defaultValue.open);
  const [searchTemp, setSearchTemp] = useState<string>(defaultValue.searchTemp);
  const [searchApplied, setSearchApplied] = useState<SearchAppliedProps>(defaultValue.searchApplied);
  const { handleSelectedTable } = useJobDetailsProvider();

  const [content, setContent] = useState<React.ReactNode | null>(null);

  const handleOpen = (isOpen: boolean) => {
    if (!isOpen) {
      setOpen(isOpen);
      setSearchTemp('');
    }

    /** If search already applied then  */
    if (open && searchApplied) {
      setSearchTemp(searchApplied.search.toString());
    }

    setOpen(isOpen);
  };
  const handleSearchTemp = (searchVal: string) => {
    setSearchTemp(searchVal);
  };
  const handleSearchApplied = (searchVal: SearchAppliedProps) => {
    // /** Before setting new value set selected table to main one */
    // Its (set selected table) not gonna work when we will move to 'Delivery info' and 'Geo Delivery Data' tabs.
    // handleSelectedTable({ row: 'ACCOUNTS', column: 'total' });
    setSearchApplied(searchVal);
  };
  const handleClearSearch = () => {
    setSearchTemp(defaultValue.searchTemp);
    setSearchApplied(defaultValue.searchApplied);
  };
  const handleContent = (contentNode: React.ReactNode | null) => {
    setContent(contentNode);
  };

  const memoizedValue: GlobalSearchProviderProps = useMemo(
    () => ({
      open,
      handleOpen,
      searchTemp,
      searchApplied,
      handleSearchTemp,
      handleSearchApplied,
      content,
      handleContent,
    }),
    [open, handleOpen, searchTemp, searchApplied, handleSearchTemp, handleSearchApplied, content, setContent],
  );

  return (
    <GlobalSearchContext.Provider value={memoizedValue}>
      <Drawer direction="right" open={open} onOpenChange={handleOpen}>
        <DrawerContent direction="right">
          <ScrollArea className="h-screen">
            <DrawerHeader>
              <div className="flex justify-between items-center">
                <DrawerTitle className="flex items-center gap-3">
                  <div className="bg-addition5 text-white rounded-lg p-1.5">
                    <Icons.Search />
                  </div>
                  Search
                </DrawerTitle>
                <DrawerClose asChild>
                  <Cross1Icon className="cursor-pointer" />
                </DrawerClose>
              </div>
              <DrawerDescription>Global search</DrawerDescription>
            </DrawerHeader>

            <div className="main-content p-4">
              <Input
                data-vaul-no-drag
                placeholder="Search"
                icon={
                  <>
                    <Icons.Unapprove className="cursor-pointer" onClick={handleClearSearch} />
                    <Icons.Search />
                  </>
                }
                autoFocus
                variant="white"
                inputClassName="pr-16"
                value={searchTemp}
                onChange={e => {
                  if (e.target.value === '') {
                    handleClearSearch();
                  }
                  setSearchTemp(e.target.value);
                }}
              />
              {content}
            </div>

            <DrawerFooter className="flex justify-end items-end">
              <Separator />
              <Button
                onClick={() => {
                  handleSelectedTable({ row: 'ACCOUNTS', column: 'total' });
                  handleSearchApplied({ search: searchTemp, field: '' });
                  setOpen(false);
                }}
                className="max-w-40"
              >
                Search All
              </Button>
            </DrawerFooter>
          </ScrollArea>
        </DrawerContent>
      </Drawer>
      {children}
    </GlobalSearchContext.Provider>
  );
}

export default GlobalSearchProvider;

export const useGlobalSearchProvider = (): GlobalSearchProviderProps => useContext(GlobalSearchContext);
